import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  type OnChanges,
  type OnDestroy,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type { CreateInventoryType } from '../../../shared/models/api/inventory-type.model';
import type { ControlsOf } from '../../../shared/models/controls-of.model';
import type { NgChanges } from '../../../shared/models/ng-changes.model';
import { InventoryTypeService } from '../../../shared/services/api/inventory-type.service';
import { FormIdService } from '../../../shared/services/form-id.service';
import { OpaValidators } from '../../../shared/validators/opa.validators';
import type { CreateInventoryTypeFormValue } from './inventory-type-create.models';

@Component({
  selector: 'opa-inventory-type-create',
  imports: [CommonModule, PorscheDesignSystemModule, ReactiveFormsModule, LetDirective],
  templateUrl: './inventory-type-create.component.html',
  styleUrl: './inventory-type-create.component.scss',
})
export class InventoryTypeCreateComponent implements OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly inventoryTypeService = inject(InventoryTypeService);
  private readonly toastManager = inject(ToastManager);
  readonly formId = inject(FormIdService).getId();

  @Input({ required: true }) open = false;
  @Output() dismiss = new EventEmitter<boolean | undefined>();

  private readonly subSink = new SubSink();
  readonly form = this.buildForm();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());

  ngOnChanges(changes: NgChanges<InventoryTypeCreateComponent>): void {
    if (changes.open && this.open) this.form.reset();
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(success?: boolean): void {
    this.dismiss.emit(success);
  }

  submit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.next(true);
    const formValue = this.form.getRawValue();
    const type = this.formValueToType(formValue);
    this.subSink.sink = this.inventoryTypeService
      .createInventoryType(type)
      .pipe(
        tap({
          next: (createdType) => {
            this.toastManager.addMessage({
              text: `Type '${createdType.description}' created`,
              state: 'success',
            });
            this.submitting.next(false);
            this.close(true);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<CreateInventoryTypeFormValue>> {
    const form = this.formBuilder.group<ControlsOf<CreateInventoryTypeFormValue>>({
      name: this.formBuilder.control(null, [
        OpaValidators.required(),
        OpaValidators.isNormalized(),
      ]),
      description: this.formBuilder.control(null, OpaValidators.required()),
      imeiRequired: this.formBuilder.control(null),
    });

    return form;
  }

  private formValueToType(form: CreateInventoryTypeFormValue): CreateInventoryType {
    const type: CreateInventoryType = {
      description: form.description!,
      name: form.name!,
      imeiRequired: !!form.imeiRequired,
    };
    return type;
  }
}
